import { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { getEsignatureIntegrations } from '~/common/constants/integrations';
import { EsignatureType } from '~/enums/EsignatureType';
import { TicketStageType } from '~/enums/TicketStageType';
import { useHandleEsignAuthenticateAction } from '~/features/ticket/actions/useHandleEsignAuthenticateAction';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';

import { EnrichedGroupNode } from '../../helpers/NodeEnricher/NodeEnricher';
import { TreeBrowserNode } from '../../helpers/TreeBrowser/types';
import { getEsignatureBundleTooltip } from '../helpers/getEsignatureBundleTooltip';
import { SelectedTicket } from '../types';

const messages = defineMessages({
  bundleEsignatureAction: {
    id: 'WorkflowGroupNodesTable.bundleEsignatureAction',
    defaultMessage: 'Send to {providerName}',
    description:
      'Bulk action label for sending tickets to e-signature as a bundle',
  },
  bundleEsignatureDescription: {
    id: 'WorkflowGroupNodesTable.bundleEsignatureDescription',
    defaultMessage: 'Bundle documents into one signature packet',
    description: 'Description for the bundle e-signature bulk action',
  },
  bundleEsignatureTooltipNoTickets: {
    id: 'WorkflowGroupNodesTable.bundleEsignatureTooltipNoTickets',
    defaultMessage: 'Select tickets, not documents',
    description: 'Tooltip when only documents are selected and no tickets',
  },
  bundleEsignatureTooltipMinTickets: {
    id: 'WorkflowGroupNodesTable.bundleEsignatureTooltipMinTickets',
    defaultMessage: 'Select at least 2 tickets',
    description: 'Tooltip when less than 2 tickets are selected',
  },
  bundleEsignatureTooltipMixedSelection: {
    id: 'WorkflowGroupNodesTable.bundleEsignatureTooltipMixedSelection',
    defaultMessage: 'Only tickets can be bundled',
    description: 'Tooltip when both tickets and documents are selected',
  },
  bundleEsignatureTooltipNotSignStage: {
    id: 'WorkflowGroupNodesTable.bundleEsignatureTooltipNotSignStage',
    defaultMessage: 'Ticket must be in Sign stage',
    description: 'Tooltip when any selected ticket is not in Sign stage',
  },
});

export interface EsignatureBundleModalState {
  isOpen: boolean;
  tickets: SelectedTicket[];
  providerType: EsignatureType | null;
}

export const useEsignatureBundleAction = (
  nodes: TreeBrowserNode<EnrichedGroupNode>[],
) => {
  const intl = useIntl();
  const hasEsignatureBundle = useFlag(FlagType.EsignatureBundle);
  const { handleEsignAuthenticateAction } = useHandleEsignAuthenticateAction();
  const { data: esignatureTokenStatus } = api.endpoints.getTokenStatus.useQuery(
    {},
  );

  const [modalState, setModalState] = useState<EsignatureBundleModalState>({
    isOpen: false,
    tickets: [],
    providerType: null,
  });

  const closeModal = useCallback(() => {
    setModalState({
      isOpen: false,
      tickets: [],
      providerType: null,
    });
  }, []);

  const getEsignatureBundleAction = useCallback(
    (selectedIds: string[]) => {
      if (!hasEsignatureBundle || !esignatureTokenStatus?.providerType) {
        return null;
      }

      const selectedNodes = nodes.filter(
        (node) =>
          selectedIds.includes(node.id as string) && !node.meta.isLoadMoreNode,
      );

      const tickets = selectedNodes.filter(
        (node) => node.attributes.resourceType === 'ticket',
      );
      const documents = selectedNodes.filter(
        (node) => node.attributes.resourceType === 'document',
      );
      const allInSignStage = tickets.every(
        (node) => node.enrichedFields?.stage === TicketStageType.Sign,
      );

      const tooltipType = getEsignatureBundleTooltip(
        tickets.length,
        documents.length,
        allInSignStage,
      );

      const tooltip =
        tooltipType &&
        intl.formatMessage(
          {
            noTickets: messages.bundleEsignatureTooltipNoTickets,
            minTickets: messages.bundleEsignatureTooltipMinTickets,
            mixedSelection: messages.bundleEsignatureTooltipMixedSelection,
            notSignStage: messages.bundleEsignatureTooltipNotSignStage,
          }[tooltipType],
        );
      const providerName =
        getEsignatureIntegrations()[esignatureTokenStatus.providerType].name;

      return {
        icon: 'workflow' as const,
        label: intl.formatMessage(messages.bundleEsignatureAction, {
          providerName,
        }),
        description: intl.formatMessage(messages.bundleEsignatureDescription),
        tooltip,
        disabled: Boolean(tooltip),
        onClick: () => {
          const selectedTickets: SelectedTicket[] = tickets.map((node) => ({
            ticketId: node.attributes.resourceId as string,
            name: (node.enrichedFields?.name as string) ?? '',
          }));
          handleEsignAuthenticateAction(() => {
            setModalState({
              isOpen: true,
              tickets: selectedTickets,
              providerType: esignatureTokenStatus.providerType,
            });
          });
        },
      };
    },
    [
      hasEsignatureBundle,
      esignatureTokenStatus,
      nodes,
      intl,
      handleEsignAuthenticateAction,
    ],
  );

  return { getEsignatureBundleAction, modalState, closeModal };
};
